import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import * as signalR from "@microsoft/signalr";
import notificationSound from "../assets/bellNotification.wav";
import { useNotification } from "context/NotificationContext";
import { notification } from "antd";
import { getAdminRecentOrders, getAgentManagerRecentOrders} from "./OrderService";
import { useUsersCampaigns } from "hooks/UseCampaigns";
import { useAuth } from "components/auth/AuthContext";

const API_BASE_URL = process.env.REACT_APP_TRADEPLUS_URL;

const OrderContext = createContext();

export const OrderProvider = ({ children }) => {
  const { getUserInfo } = useAuth();
  const [orders, setOrders] = useState([]);
  const connectionRef = useRef(null);
  const { addNotification } = useNotification();
  const [enableSound, setEnableSound] = useState(true);
  const [enablePopup, setEnablePopup] = useState(true);
  const { id, role: userRole, fulfillmentCenterId } = getUserInfo();

  /////FETCH CAMPAIGN IDS/////
  const {
    data: userCampaignsData = [],
    isError: isUserCampaignsError,
    error: userCampaignsError,
  } = useUsersCampaigns(id);

  // ✅ Memoize campaignIds so it doesn’t re-render each time
  const campaignIds = useMemo(() => {
    // If the user has campaign assignments, ignore fulfillmentCenterId
    if (userCampaignsData.length > 0) {
      return Array.from(
        new Set(userCampaignsData.map((item) => item.campaignId))
      );
    }

    // Otherwise fallback to fulfillmentCenterId
    return fulfillmentCenterId ? [fulfillmentCenterId] : [];
  }, [userCampaignsData, fulfillmentCenterId]);

  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(
        `${API_BASE_URL}/orderHub?campaignId=${campaignIds.join(
          ","
        )}&userType=${userRole}`
      )
      .withAutomaticReconnect()
      .build();

    // console.log("Order Provider Ids", campaignIds);
    // console.log("Role", userRole);

    connection
      .start()
      .then(() => {
        console.log("✅ Connected to SignalR Hub");

        connection.on("ReceiveOrder", async (order) => {
          console.log("📦 New Order (raw):", order);
          addNotification(order);

          // Play sound
          if (enableSound) {
            const audio = new Audio(notificationSound);
            audio.play().catch((err) => console.warn("Sound error:", err));
          }

          if (enablePopup) {
            notification.open({
              message: "New Order Received",
              description: `Order #${order.id} from ${
                order.outletName || "an outlet"
              }`,
              duration: 2,
              placement: "topRight",
            });
          }

          // Get campaign IDs from localStorage or props
          const storedCampaignIds =
            JSON.parse(localStorage.getItem("fulfillmentCenterId")) || [];
          const activeCampaignIds =
            campaignIds.length > 0 ? campaignIds : storedCampaignIds;

          // 🟡 Trigger full refresh of recent orders
          try {
            const res =
              userRole === "admin"
                ? await getAdminRecentOrders({})
                : await getAgentManagerRecentOrders({ campaignIds: activeCampaignIds });

            setOrders(res);
          } catch (err) {
            console.error("Failed to fetch recent orders:", err);
          }
        });
      })
      .catch((err) => console.error("❌ SignalR Error:", err));

    connectionRef.current = connection;

    return () => {
      connection.stop();
    };
  }, [userRole, campaignIds, addNotification, enableSound, enablePopup]);

  const errors = [
    isUserCampaignsError &&
      `User Campaign Error:: ${userCampaignsError.message}`,
  ].filter(Boolean);

  return (
    <>
      {errors.length > 0 && (
        <div style={{ color: "red", marginBottom: "1rem" }}>
          Some data failed to load:
          <ul>
            {errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}
      <OrderContext.Provider
        value={{
          orders,
          setOrders,
          enableSound,
          setEnableSound,
          enablePopup,
          setEnablePopup,
        }}
      >
        {children}
      </OrderContext.Provider>
    </>
  );
};

export const useOrders = () => useContext(OrderContext);
